import {
  Alert,
  CircularProgress,
  FormControl,
  Grid,
  Modal,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import Button from '../../components/CustomButtons/Button';
import GridItem from '../../components/Grid/GridItem';
import Tile from '../../components/Tile/Tile';
import { setRefetch } from '../../redux/actions';
import { getS3ImageUrl, setFieldValue, validateValue, vendorContactRoleHumanReadable } from '../../utils';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import InfoIcon from '@mui/icons-material/Info';
import Autocomplete from '@mui/material/Autocomplete';
import CreateLocationModal from './CreateLocationModal';
import TableSearch from 'components/Table/TableSearch';
import _ from 'lodash';
import LinearProgress from '@mui/material/LinearProgress';
import PersonalLinkAndQRCode from '../../components/PersonalLinkAndQRCode';
import { theme } from '../../theme';
import Table from 'components/Table';
import TableSelect from 'components/Table/TableSelect';
import { PageContainer } from '../../components/shared/Page/PageContainer';
import { US_STATES } from '../../constants/usStates';
import { CANADIAN_PROVINCES } from '../../constants/canadianProvinces';

const config = require('../../config.js');

const useStyles = makeStyles({
  companyProfile: {
    paddingTop: '10px!important',
    paddingLeft: '10px!important',
  },
  cardStyles: {
    marginBottom: '50px !important',
  },
  buttonContainer: {
    marginBottom: 30,
    paddingLeft: '0 !important',
  },
  cardBody: {
    padding: '25px',
    '& .personalLinkAndQRCodeRoot > div': {
      margin: '15px 0',
    },
  },
  companyProfileHeader: {
    fontSize: '26px',
    fontWeight: '300',
    '& svg': {
      margin: '0 0 10px 0',
    },
  },
  companyProfileContact: {
    paddingRight: '22px',
    display: 'flex',
    alignItems: 'center',
  },
  locationTable: {
    '& .rt-noData': {
      top: '45%',
    },
  },
  locationLogo: {
    width: '300px',
    objectFit: 'contain',
  },
});

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

const Q_LOCATION_DETAILS = gql`
  query ($locationId: ID) {
    location(locationId: $locationId) {
      locationId
      locationName
      category
      accountDynamicsId
      locationParentId
      logo
      colorSchema
      salesManagerId
      financeManagerId
      creditManagerId
      ocaShowOnlyChildLocations
      ocaHidden
      address {
        addressId
        country
        address1
        address2
        city
        stateProvince
        postalCode
      }
      ocaContact {
        fullName
        dynamicsContactId
        vendorContactRole
        title
        id
      }
      contactsInfo {
        id
        dynamicsContactId
        vendorContactRole
        fullName
        title
        email
        phoneNumber
      }
      nonAssociatedContactsInfo {
        id
        dynamicsContactId
        vendorContactRole
        email
        fullName
        status
      }
      notificationAppReceiptGroup {
        notificationGroupId
        groupName
        notificationsToUserProfiles {
          fullName
          title
          id
          email
          phoneNumber
          dynamicsContactId
          vendorContactRole
          applyingForReasons
        }
      }
      parentLocation {
        locationName
        locationId
      }
      childrenLocation {
        locationName
        locationId
      }
    }
  }
`;

const UPDATE_LOCATION = gql`
  mutation ($input: UpsertLocationInput!) {
    upsertLocation(input: $input)
  }
`;

const UPDATE_NOTIFICATION_GROUP = gql`
  mutation ($input: UpsertNotificationGroupInput!) {
    upsertNotificationGroup(input: $input)
  }
`;

const DELETE_LOCATION = gql`
  mutation ($locationId: ID) {
    deleteLocation(locationId: $locationId)
  }
`;

const GET_LOCATIONS = gql`
  query locations {
    locations {
      locationId
      locationName
      category
      accountDynamicsId
      ocaContactId
      ocaContact {
        fullName
      }
      contactIds
      locationParentId
      notificationAppReceipt
    }
  }
`;

// DR-10332: Reasons for Applying are dealer-configurable via the active OCA template.
const GET_APPLYING_FOR_OPTIONS = gql`
  query PublicAccountByDynamicsId($dynamicsId: ID) {
    publicAccount(dynamicsId: $dynamicsId) {
      vendorProfile {
        ocaTemplate {
          active
          jsonDefinition
        }
      }
    }
  }
`;

// DR-10332: Extract the dealer's configured "Applying For" options from the active OCA template definition.
const extractApplyingForOptions = jsonDefinition => {
  if (!Array.isArray(jsonDefinition)) {
    return [];
  }
  for (const page of jsonDefinition) {
    for (const section of _.get(page, 'sections', []) || []) {
      for (const field of _.get(section, 'fields', []) || []) {
        const fieldName = _.get(field, 'config.fieldName');
        if (fieldName === 'applyingFor' || fieldName === 'applyingSelect') {
          const options = _.get(field, 'config.fieldOptions', []) || [];
          return options
            .filter(option => !option.isHidden && !!option.optionValue)
            .map(option => ({
              value: option.optionValue,
              label: _.get(option, 'displayLabel.en') || option.optionLabel || option.optionValue,
            }));
        }
      }
    }
  }
  return [];
};

function LocationDetails({ match, setRefetch }) {
  const locationId = match.params.locationId;
  const history = useHistory();
  const [locationReps, setLocationReps] = useState([]);
  const [selectedLocationReps, setSelectedLocationReps] = useState(null);
  const [editLocationReps, setEditLocationReps] = useState(false);
  const [notificationGroup, setNotificationGroup] = useState([]);
  const [editNotificationGroup, setEditNotificationGroup] = useState(false);
  const [locationsList, setLocationsList] = useState();
  const account = useSelector(state => state.account);
  const [filteredLocationReps, setFilteredLocationReps] = useState([]);
  const [refetching, setRefetching] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState(false);
  const [locationRepsPageNumber, setLocationRepsPageNumber] = useState(0);
  const locationRepsRoleRef = useRef({ prevSelect: undefined, currentSelect: '' });
  const locationRepsSearchRef = useRef({ prevSearch: undefined, currentSearch: '' });
  const [locationRepsSavedListSettings, setLocationRepsSavedListSettings] = useState({ page: 0, pageSize: 5 });
  const [locationRepsPageSizeValue, setLocationRepsPageSizeValue] = useState(5);

  const [filteredNotificationGroup, setFilteredNotificationGroup] = useState([]);
  const [notificationGroupPageNumber, setNotificationGroupPageNumber] = useState(0);
  const notificationsGroupRoleRef = useRef({ prevRole: undefined, currentSelect: '' });
  const notificationGroupSearchRef = useRef({ prevSearch: undefined, currentSearch: '' });
  const [notificationGroupSavedListSettings, setNotificationGroupSavedListSettings] = useState({
    page: 0,
    pageSize: 5,
  });
  const [notificationGroupPageSizeValue, setNotificationGroupPageSizeValue] = useState(5);
  const [savingNotificationGroup, setSavingNotificationGroup] = useState(false);

  const {
    data: locationDetails,
    refetch: locationDetailsRefetch,
    loading,
    error,
  } = useQuery(Q_LOCATION_DETAILS, {
    variables: {
      locationId: locationId,
    },
    fetchPolicy: 'no-cache',
  });

  // DR-10332: dealer-configurable "Reason for Applying" options for the "Notified When" condition.
  // Key off the location's owning account so it also resolves for internal/master users.
  const applyingForDynamicsId =
    _.get(locationDetails, 'location.accountDynamicsId') || _.get(account, 'dynamicsAccountId');
  const { data: applyingForData, error: applyingForError } = useQuery(GET_APPLYING_FOR_OPTIONS, {
    skip: !applyingForDynamicsId,
    variables: { dynamicsId: applyingForDynamicsId },
  });
  useEffect(() => {
    if (applyingForError) {
      console.error('GET_APPLYING_FOR_OPTIONS error:', applyingForError);
    }
  }, [applyingForError]);
  const applyingForOptions = React.useMemo(() => {
    const templates = _.get(applyingForData, 'publicAccount.vendorProfile.ocaTemplate', []) || [];
    // Prefer the active template; fall back to the first available so reasons still populate.
    const activeTemplate = templates.find(template => template && template.active) || templates[0];
    return extractApplyingForOptions(_.get(activeTemplate, 'jsonDefinition'));
  }, [applyingForData]);
  const reasonLabelByValue = React.useMemo(() => {
    const map = {};
    applyingForOptions.forEach(option => {
      map[option.value] = option.label;
    });
    return map;
  }, [applyingForOptions]);

  const [getLocations, { data: locations, refetch: locationsRefetch }] = useLazyQuery(GET_LOCATIONS, {
    context: { authRequired: true },
  });

  const [updateLocation] = useMutation(UPDATE_LOCATION, {
    context: { authRequired: true },
  });

  const [updateNotificationGroup] = useMutation(UPDATE_NOTIFICATION_GROUP, {
    context: { authRequired: true },
  });

  const [deleteLocation] = useMutation(DELETE_LOCATION, {
    context: { authRequired: true },
  });

  const formatPhone = val => {
    const digits = (val || '').replace(/[^\d]/g, '').substr(0, 10);
    if (!digits.length) {
      return '';
    }
    let ret = '(' + digits.substr(0, 3);
    if (digits.length >= 4) {
      ret += ') ' + digits.substr(3, 3);
    }
    if (digits.length >= 7) {
      ret += '-' + digits.substr(6, 4);
    }
    return ret;
  };
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [openEditLocationModal, setOpenEditLocationModal] = useState(false);
  const [fields, setFields] = useState({
    locationId: {
      value: '',
      validationStatus: '',
    },
    locationName: {
      value: '',
      validationStatus: '',
    },
    category: {
      value: '',
      validationStatus: '',
    },
    accountDynamicsId: {
      value: '',
      validationStatus: '',
    },
    ocaContactId: {
      value: '',
      validationStatus: '',
      validations: [{ type: 'required' }],
    },
    ocaContactFullName: {
      value: '',
      validationStatus: '',
      validations: [{ type: 'required' }],
    },
    ocaContactTitle: {
      value: '',
      validationStatus: '',
      validations: [{ type: 'required' }],
    },
    locationParentId: {
      value: '',
      validationStatus: '',
    },
    repsAtThisLocation: {
      value: '',
      validationStatus: '',
    },
    allReps: {
      value: '',
      validationStatus: '',
    },
    notificationGroup: {
      value: [],
    },
    notificationGroupName: {
      value: '',
    },
    notificationGroupId: {
      value: '',
    },
    parentLocation: {
      value: '',
    },
    childrenLocation: {
      value: '',
    },
    logo: {
      value: '',
    },
    financeManager: {
      value: '',
    },
    creditManager: {
      value: '',
    },
    salesManager: {
      value: '',
    },
    ocaLink: {
      value: '',
    },
  });
  const [formValid, setFormValid] = useState(false);
  const checkFormValidation = () => {
    let status = true;
    Object.keys(fields).forEach(fieldName => {
      if (!!fields[fieldName].validations && fields[fieldName].validations.length) {
        const value = fields[fieldName].value;
        const validations = fields[fieldName].validations;
        fields[fieldName].validationStatus = validateValue(value, validations) ? 'success' : 'error';
        if (fields[fieldName].validationStatus !== 'success') {
          status = false;
        }
      }
    });
    setFormValid(status);
    return formValid;
  };

  const setValue = (value, fieldName) => setFieldValue(value, fieldName, fields, setFields);

  const handleCloseEditLocation = async upsertModalClosedValue => {
    setOpenEditLocationModal(false);

    if (_.get(upsertModalClosedValue, 'locationId', undefined)) {
      setRefetching(true);
      await locationDetailsRefetch();
      setRefetching(false);
    }
  };

  const handleEditLocationClick = () => {
    setEditLocationReps(!editLocationReps);
  };

  const handleEditNotificationGroupClick = () => {
    setEditNotificationGroup(!editNotificationGroup);
  };

  const handleCancelEditLocationClick = () => {
    setSelectedLocationReps(
      fields.repsAtThisLocation.value.map(item => {
        item.value = item.id;
        return item.value;
      })
    );
    setEditLocationReps(false);
  };

  const handleCancelEditNotificationClick = () => {
    setNotificationGroup(
      fields.notificationGroup.value.map(item => {
        item.value = item.id;
        return item.value;
      })
    );
    setEditNotificationGroup(false);
  };

  const handleSaveEditLocationClick = async () => {
    try {
      await updateLocation({
        variables: {
          input: {
            locationId: fields.locationId.value,
            locationName: fields.locationName.value,
            category: fields.category.value,
            contactIds: selectedLocationReps.reduce((acc, curr) => (acc.push(curr.id), acc), []),
          },
        },
      });
      await locationDetailsRefetch();
      setSnackbarOpen(true);
      setRefetch('account', true);
    } catch (e) {
      console.log(e);
      setErrorMessage('Error updating location contacts!');
      setErrorOpen(true);
    }
    setEditLocationReps(false);
  };

  const handleSaveEditNotificationClick = async () => {
    setSavingNotificationGroup(true);
    try {
      await updateNotificationGroup({
        variables: {
          input: {
            notificationGroupId: fields.notificationGroupId.value,
            notificationMembers: notificationGroup.map(member => ({
              userProfileId: member.id,
              applyingForReasons: Array.isArray(member.applyingForReasons) ? member.applyingForReasons : [],
            })),
            groupName: fields.notificationGroupName.value
              ? fields.notificationGroupName.value
              : `${locationDetails.location.locationName} Notification Group`,
            applicationReceiptLocationId: locationDetails.location.locationId,
          },
        },
      });
      await locationDetailsRefetch();
      setSnackbarOpen(true);
      setRefetch('account', true);
    } catch (e) {
      console.log(e);
      setErrorMessage('Error updating location notification group!');
      setErrorOpen(true);
    }
    setSavingNotificationGroup(false);
    setEditNotificationGroup(false);
  };

  const handleUpdateOCAContact = async () => {
    try {
      await updateLocation({
        variables: {
          input: {
            locationId: fields.locationId.value,
            locationName: fields.locationName.value,
            category: fields.category.value,
            ocaContactId: fields.ocaContactId.value,
          },
        },
      });
      await locationDetailsRefetch();
      setSnackbarOpen(true);
      setRefetch('account', true);
    } catch (e) {
      console.log(e);
      setErrorMessage('Error updating primary contact!');
      setErrorOpen(true);
    }
  };

  const handleLocationClick = id => {
    history.push(`/locationDetail/${id}`);
  };

  const handleDeleteLocationClick = async () => {
    setDeletingLocation(true);
    console.log(`Deleting location: ${locationId}`);
    const deleteLocationResult = await deleteLocation({
      variables: {
        locationId,
      },
    });
    if (deleteLocationResult) {
      history.goBack();
    } else {
      setDeletingLocation(false);
    }
  };

  useEffect(() => {
    if (locationReps) {
      const filteredData = filterLocationReps(locationReps);
      setFilteredLocationReps(filteredData);
    }
  }, [locationReps, locationRepsSearchRef.current.currentSearch, locationRepsRoleRef.current.currentSelect]);

  useEffect(() => {
    if (fields.notificationGroup.value) {
      setFilteredNotificationGroup(filterNotificationGroup(fields.notificationGroup.value));
    }
  }, [
    fields.notificationGroup.value,
    notificationGroupSearchRef.current.currentSearch,
    notificationsGroupRoleRef.current.currentSelect,
  ]);

  const filterNotificationGroup = notificationGroup => {
    const { currentSearch } = notificationGroupSearchRef.current;
    const { currentSelect: currentRoleSelect } = notificationsGroupRoleRef.current;

    if (notificationGroupSearchRef.length === 0) {
      return notificationGroup;
    }

    const lowerCaseSearch = currentSearch.toLocaleLowerCase().trim();
    let searchData = notificationGroup.filter(el => {
      if (
        typeof el.fullName === 'string' ||
        typeof el.title === 'string' ||
        typeof el.email === 'string' ||
        typeof el.phoneNumber === 'string'
      ) {
        return (
          (el.fullName || '').toLowerCase().includes(lowerCaseSearch) ||
          (el.title || '').toLowerCase().includes(lowerCaseSearch) ||
          (el.email || '').toLowerCase().includes(lowerCaseSearch) ||
          (el.phoneNumber || '').replace(/[^0-9]/g, '').includes(lowerCaseSearch)
        );
      }

      return false;
    });

    if (currentRoleSelect?.length !== 0) {
      searchData = searchData.filter(el => el.vendorContactRole === currentRoleSelect);
    }

    return [...searchData];
  };

  const filterLocationReps = locationReps => {
    const { currentSearch } = locationRepsSearchRef.current;
    const { currentSelect: currentRoleSelect } = locationRepsRoleRef.current;

    if (locationRepsSearchRef.length === 0) {
      return locationReps;
    }

    const lowerCaseSearch = currentSearch.toLocaleLowerCase().trim();

    let searchData = locationReps.filter(el => {
      if (
        typeof el.fullName === 'string' ||
        typeof el.title === 'string' ||
        typeof el.email === 'string' ||
        typeof el.phoneNumber === 'string'
      ) {
        return (
          (el.fullName || '').toLowerCase().includes(lowerCaseSearch) ||
          (el.title || '').toLowerCase().includes(lowerCaseSearch) ||
          (el.email || '').toLowerCase().includes(lowerCaseSearch) ||
          (el.phoneNumber || '').replace(/[^0-9]/g, '').includes(lowerCaseSearch)
        );
      }

      return false;
    });

    if (currentRoleSelect?.length !== 0) {
      searchData = searchData.filter(el => el.vendorContactRole === currentRoleSelect);
    }

    return [...searchData];
  };

  useEffect(() => {
    if (locationDetails && locationDetails.location) {
      var repsAtThisLocation;
      var allReps;
      var notificationReps;

      if (locationDetails.location.contactsInfo) {
        repsAtThisLocation = locationDetails.location.contactsInfo.map(item => {
          try {
            item.value = item.id;
            item.phoneNumber = item.phoneNumber ? formatPhone(item.phoneNumber) : null;
            if (item.title == null) {
              item.label = item.fullName ? item.fullName : item.email;
            } else {
              item.label = item.fullName ? item.fullName : item.email + ', ' + item.title;
            }
            return item;
          } catch (err) {
            console.error(err);
            return null;
          }
        });
        notificationReps = locationDetails.location.notificationAppReceiptGroup.notificationsToUserProfiles.map(
          item => {
            try {
              item.value = item.id;
              item.applyingForReasons = Array.isArray(item.applyingForReasons) ? item.applyingForReasons : [];
              if (item.title == null) {
                item.label = item.fullName ? item.fullName : item.email;
              } else {
                item.label = item.fullName ? item.fullName : item.email + ', ' + item.title;
              }
              return item;
            } catch (err) {
              console.error(err);
              return null;
            }
          }
        );
        setLocationReps(repsAtThisLocation);
        setSelectedLocationReps(repsAtThisLocation);
        setNotificationGroup(notificationReps);
      } else {
        setLocationReps([]);
      }
      if (locationDetails.location.nonAssociatedContactsInfo) {
        allReps = locationDetails.location.nonAssociatedContactsInfo.map(item => {
          try {
            item.value = item.id;
            if (item.title == null) {
              item.label = item.fullName ? item.fullName : item.email;
            } else {
              item.label = item.fullName ? item.fullName : item.email + ', ' + item.title;
            }
            return item;
          } catch (err) {
            console.error(err);
            return null;
          }
        });
        allReps = [...repsAtThisLocation, ...allReps];
      }
      setFields({
        locationId: {
          value: locationDetails.location.locationId ? locationDetails.location.locationId : '',
          validationStatus: '',
        },
        locationName: {
          value: locationDetails.location.locationName ? locationDetails.location.locationName : '',
          validationStatus: '',
        },
        category: {
          value: locationDetails.location.category ? locationDetails.location.category : '',
          validationStatus: '',
        },
        logo: {
          value: locationDetails.location.logo,
          validationStatus: '',
        },
        salesManager: {
          value: locationDetails.location.salesManagerId
            ? _.find(account.contacts, { id: locationDetails.location.salesManagerId })
            : null,
          validationStatus: '',
        },
        financeManager: {
          value: locationDetails.location.financeManagerId
            ? _.find(account.contacts, { id: locationDetails.location.financeManagerId })
            : null,
          validationStatus: '',
        },
        creditManager: {
          value: locationDetails.location.creditManagerId
            ? _.find(account.contacts, { id: locationDetails.location.creditManagerId })
            : null,
          validationStatus: '',
        },
        accountDynamicsId: {
          value: locationDetails.location.accountDynamicsId ? locationDetails.location.accountDynamicsId : '',
          validationStatus: '',
        },
        ocaContactId: {
          value: locationDetails.location.ocaContact ? locationDetails.location.ocaContact.id : '',
          validationStatus: '',
          validations: [{ type: 'required' }],
        },
        locationParentId: {
          value: locationDetails.location.locationParentId ? locationDetails.location.locationParentId : '',
          validationStatus: '',
        },
        ocaContactFullName: {
          value: locationDetails.location.ocaContact ? locationDetails.location.ocaContact.fullName : '',
          validationStatus: '',
        },
        ocaContactTitle: {
          value: locationDetails.location.ocaContact ? locationDetails.location.ocaContact.title : '',
          validationStatus: '',
        },
        repsAtThisLocation: {
          value: repsAtThisLocation,
          validationStatus: '',
        },
        allReps: {
          value: allReps,
          validationStatus: '',
        },
        ocaLink: {
          value:
            config.REACT_APP_OCA_BASE_URL +
            '/?vendorGUID=' +
            (locationDetails.location.accountDynamicsId
              ? locationDetails.location.accountDynamicsId
              : _.get(account, 'dynamicsAccountId', null)) +
            '&locationId=' +
            locationDetails.location.locationId,
        },
        notificationGroup: {
          value: locationDetails.location.notificationAppReceiptGroup
            ? locationDetails.location.notificationAppReceiptGroup.notificationsToUserProfiles
            : [],
        },
        notificationGroupName: {
          value: locationDetails.location.notificationAppReceiptGroup
            ? locationDetails.location.notificationAppReceiptGroup.groupName
            : '',
        },
        notificationGroupId: {
          value: locationDetails.location.notificationAppReceiptGroup
            ? locationDetails.location.notificationAppReceiptGroup.notificationGroupId
            : '',
        },
        parentLocation: {
          value: locationDetails.location.parentLocation ? locationDetails.location.parentLocation : '',
        },
        childrenLocation: {
          value: locationDetails.location.childrenLocation ? locationDetails.location.childrenLocation : '',
        },
      });
    }

    if (!!account && account.dynamicsAccountId) {
      getLocations();
    }
  }, [locationDetails]);

  useEffect(() => {
    if (_.get(locations, 'locations', null)) {
      setLocationsList([...locations.locations]);
    }
  }, [locations]);

  const classes = useStyles();

  const handleChangeLocationReps = value => {
    setSelectedLocationReps(value);
  };

  const handleChangeNotificationReps = value => {
    setNotificationGroup(
      value.map(item => ({
        ...item,
        applyingForReasons: Array.isArray(item.applyingForReasons) ? item.applyingForReasons : [],
      }))
    );
  };

  // DR-10332: update a single recipient's "Notified When" reasons.
  const handleChangeMemberReasons = (userId, reasonValues) => {
    setNotificationGroup(prev =>
      prev.map(item => (item.id === userId ? { ...item, applyingForReasons: reasonValues } : item))
    );
  };

  const handleClose = () => {
    setSnackbarOpen(false);
    setErrorOpen(false);
  };

  const getEditButtonText = () => (!!fields.allReps.value && fields.allReps.value.length > 1 ? 'Edit' : '');

  const handleUserClick = repId => {
    history.push(`/profile/${repId}`);
  };

  return (
    <PageContainer loading={loading} error={error}>
      {deletingLocation && (
        <div>
          Deleting Location
          <LinearProgress size={34} />
        </div>
      )}
      {locationDetails && !refetching ? (
        <>
          <Grid container spacing={1} className={classes.companyProfile}>
            <Grid container>
              <GridItem
                xs={12}
                sm={locationDetails.location.category === 'Master' ? 10 : 8}
                md={locationDetails.location.category === 'Master' ? 10 : 8}
                lg={locationDetails.location.category === 'Master' ? 10 : 8}
                className={classes.buttonContainer}
              >
                <Button className="formBtn" color="primary" variant="outlined" onClick={() => history.goBack()}>
                  Back
                </Button>
              </GridItem>

              <GridItem xs={12} sm={2} md={2} lg={2} className={classes.buttonContainer}>
                <div style={{ textAlign: 'right' }}>
                  <Button
                    className="formBtn"
                    color="primary"
                    variant="outlined"
                    onClick={() => setOpenEditLocationModal(true)}
                    style={{ padding: '0px !important' }}
                  >
                    Edit Location
                  </Button>
                </div>
              </GridItem>

              {locationDetails.location.category !== 'Master' && (
                <GridItem xs={12} sm={2} md={2} lg={2} className={classes.buttonContainer}>
                  <Button className="formBtn" color="danger" variant="outlined" onClick={handleDeleteLocationClick}>
                    Delete Location
                  </Button>
                </GridItem>
              )}
            </Grid>
            <Tile className={classes.cardStyles} classBody={classes.cardBody}>
              <Grid container spacing={2}>
                <Grid item container xs={12} sm={6} md={6} lg={6}>
                  <Grid container alignContent="flex-start" spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant={'h5'} className={classes.companyProfileHeader}>
                        Location Info
                      </Typography>
                    </Grid>
                    <Grid item lg={12} xl={12} md={12} sm={12} xs={12}>
                      <b>Location Name: </b> {fields.locationName.value}
                    </Grid>
                    <Grid item lg={12} xl={12} md={12} sm={12} xs={12}>
                      <b>Category: </b> {fields.category.value}
                    </Grid>
                    {!!fields.parentLocation.value && (
                      <Grid item lg={12} xl={12} md={12} sm={12} xs={12}>
                        <b>Parent Location: </b>{' '}
                        {fields.parentLocation.value ? fields.parentLocation.value.locationName : null}
                      </Grid>
                    )}
                    {!!fields.childrenLocation.value && fields.childrenLocation.value.length ? (
                      <Grid item lg={12} xl={12} md={12} sm={12} xs={12}>
                        <b>Child Locations: </b>
                        {fields.childrenLocation.value.map(({ locationId, locationName }, i) => (
                          <span
                            key={i}
                            style={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                            onClick={() => handleLocationClick(locationId)}
                          >
                            {locationName},
                          </span>
                        ))}
                      </Grid>
                    ) : (
                      <Grid item lg={12} xl={12} md={12} sm={12} xs={12}>
                        <b>Child Locations: </b> None
                      </Grid>
                    )}
                    {locationDetails.location.address && (
                      <>
                        <Grid item xs={12} style={{ marginTop: 16 }}>
                          <b>Address:</b>
                        </Grid>
                        <Grid item lg={12} xl={12} md={12} sm={12} xs={12} style={{ paddingTop: 4 }}>
                          {locationDetails.location.address.address1}
                        </Grid>
                        {locationDetails.location.address.address2 && (
                          <Grid item lg={12} xl={12} md={12} sm={12} xs={12} style={{ paddingTop: 4 }}>
                            {locationDetails.location.address.address2}
                          </Grid>
                        )}
                        <Grid item lg={12} xl={12} md={12} sm={12} xs={12} style={{ paddingTop: 4 }}>
                          {locationDetails.location.address.city}, {locationDetails.location.address.stateProvince}{' '}
                          {locationDetails.location.address.postalCode},{' '}
                          {locationDetails.location.address.country === 'US' ? 'United States' : 'Canada'}
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <Typography variant={'h5'} className={classes.companyProfileHeader} style={{ marginTop: 16 }}>
                        Contacts
                      </Typography>
                    </Grid>
                    <Grid item lg={12} xl={12} md={12} sm={12} xs={12}>
                      <b>Primary Contact: </b>{' '}
                      {fields.ocaContactFullName.value ? (
                        _.find(fields.allReps.value, { id: fields.ocaContactId.value }) ? (
                          <a
                            style={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                            href={`/profile/${
                              _.find(fields.allReps.value, { id: fields.ocaContactId.value }).dynamicsContactId
                            }`}
                          >
                            {fields.ocaContactFullName.value}
                          </a>
                        ) : (
                          fields.ocaContactFullName.value
                        )
                      ) : (
                        'None'
                      )}
                    </Grid>
                    <Grid item lg={12} xl={12} md={12} sm={12} xs={12}>
                      <b>Finance Manager: </b>{' '}
                      {fields.financeManager.value ? (
                        fields.financeManager.value.dynamicsContactId ? (
                          <a
                            style={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                            href={`/profile/${fields.financeManager.value.dynamicsContactId}`}
                          >
                            {fields.financeManager.value.fullName}
                          </a>
                        ) : (
                          fields.financeManager.value.fullName
                        )
                      ) : (
                        'None'
                      )}
                    </Grid>
                    <Grid item lg={12} xl={12} md={12} sm={12} xs={12}>
                      <b>Sales Manager: </b>
                      {fields.salesManager.value ? (
                        fields.salesManager.value.dynamicsContactId ? (
                          <a
                            style={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                            href={`/profile/${fields.salesManager.value.dynamicsContactId}`}
                          >
                            {fields.salesManager.value.fullName}
                          </a>
                        ) : (
                          fields.salesManager.value.fullName
                        )
                      ) : (
                        'None'
                      )}
                    </Grid>
                    <Grid item lg={12} xl={12} md={12} sm={12} xs={12}>
                      <b>Credit Manager: </b>{' '}
                      {fields.creditManager.value ? (
                        fields.creditManager.value.dynamicsContactId ? (
                          <a
                            style={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                            href={`/profile/${fields.creditManager.value.dynamicsContactId}`}
                          >
                            {fields.creditManager.value.fullName}
                          </a>
                        ) : (
                          fields.creditManager.value.fullName
                        )
                      ) : (
                        'None'
                      )}
                    </Grid>
                    {fields.logo.value ? (
                      <Grid item xs={12} className={classes.locationLogoContainer}>
                        <img
                          src={getS3ImageUrl(fields.logo.value)}
                          alt="Location Logo"
                          className={classes.locationLogo}
                        />
                      </Grid>
                    ) : (
                      <Grid item lg={12} xl={12} md={12} sm={12} xs={12}>
                        <b>Location Logo: </b> None
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <PersonalLinkAndQRCode
                  personalLink={fields.ocaLink.value}
                  linkHeader="Location Credit Application Link"
                />
              </Grid>
            </Tile>
            <Tile
              title={
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <Stack direction="row" alignItems="center">
                    <Typography variant="subtitle1" component="span">
                      Staff At This Location
                    </Typography>
                    <HtmlTooltip
                      title={
                        'Identify the representatives customers will be able to select on your credit application for this location.'
                      }
                    >
                      <InfoIcon color="primary" />
                    </HtmlTooltip>
                  </Stack>

                  <Stack direction="row" gap="1rem" alignItems="baseline">
                    <TableSearch
                      setPageNumber={setLocationRepsPageNumber}
                      setSavedListSettings={setLocationRepsSavedListSettings}
                      searchRef={locationRepsSearchRef}
                      savedListSettings={locationRepsSavedListSettings}
                    />
                    <TableSelect
                      setPageNumber={setLocationRepsPageNumber}
                      setSavedListSettings={setLocationRepsSavedListSettings}
                      selectRef={locationRepsRoleRef}
                      savedListSettings={locationRepsSavedListSettings}
                      id="location-role"
                      label="Role"
                      options={Object.entries(vendorContactRoleHumanReadable).map(ent => ({
                        value: ent[0],
                        text: ent[1],
                      }))}
                      width={200}
                    />
                  </Stack>
                </div>
              }
              titleButtonText={getEditButtonText()}
              titleButtonFunc={handleEditLocationClick}
              className={classes.cardStyles}
              classBody={classes.cardBody}
            >
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Grid container justifyContent="space-between" alignItems="flex-end">
                    <Grid item md={12}>
                      <Table
                        color="primary"
                        data={filteredLocationReps}
                        noDataText={<></>}
                        getTrProps={(state, rowInfo, column, instance) => ({
                          style: { cursor: 'pointer' },
                          onClick: e => handleUserClick(rowInfo.original.dynamicsContactId),
                        })}
                        columns={[
                          {
                            Header: 'Name',
                            accessor: 'fullName',
                            resizable: false,
                          },
                          {
                            Header: 'Title',
                            accessor: 'title',
                          },
                          {
                            Header: 'Role',
                            accessor: 'vendorContactRole',
                            Cell: ({ value }) => (value ? vendorContactRoleHumanReadable[value] : null),
                          },
                          {
                            Header: 'Email',
                            accessor: 'email',
                          },
                          {
                            Header: 'Phone',
                            accessor: 'phoneNumber',
                          },
                        ]}
                        onPageChange={changePage => {
                          setLocationRepsPageNumber(changePage);
                        }}
                        onPageSizeChange={changePageSize => {
                          setLocationRepsPageSizeValue(changePageSize);
                          setLocationRepsPageNumber(0);
                        }}
                        page={locationRepsPageNumber}
                        pageSize={locationRepsPageSizeValue}
                        defaultPageSize={locationRepsSavedListSettings.pageSize}
                        showPaginationTop={false}
                        showPaginationBottom={true}
                        customCellClasses={[classes.right]}
                        customClassesForCells={[5]}
                        className={classes.locationTable}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {editLocationReps && (
                  <Grid item xs={12}>
                    <br />
                    <Grid container justifyContent="space-between" alignItems="flex-end">
                      <Grid item md={12}>
                        <p style={{ margin: '0 0 16px' }}>
                          <b>Edit Staff For These Notifications</b>
                        </p>
                        <FormControl fullWidth>
                          <Autocomplete
                            multiple
                            size="small"
                            value={selectedLocationReps}
                            options={fields.allReps.value
                              .filter(rep => !selectedLocationReps.find(slr => slr.id === rep.id))
                              .filter(user => user.status !== 'blocked')}
                            onChange={(event, newValue) => {
                              handleChangeLocationReps(newValue);
                            }}
                            isOptionEqualToValue={(option, value) => {
                              return option.id === value;
                            }}
                            getOptionLabel={option => {
                              return option.label;
                            }}
                            renderInput={params => (
                              <TextField {...params} variant="standard" label="Reps" placeholder="" />
                            )}
                          />
                        </FormControl>
                        <br />
                        <br />
                        <Grid item md={12} className={classes.companyProfileContact}>
                          <Grid
                            container
                            style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', gap: 8 }}
                          >
                            <Button onClick={handleCancelEditLocationClick}>Cancel</Button>
                            <Button color="primary" variant="contained" onClick={handleSaveEditLocationClick}>
                              Save
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Tile>

            <Tile
              title={
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <Stack direction="row" alignItems="center">
                    <Typography variant="subtitle1" component="span">
                      Notifications
                    </Typography>
                    <HtmlTooltip
                      title={
                        'Selected recipients will be notified via email whenever an online credit application is submitted for this location.'
                      }
                    >
                      <InfoIcon color="primary" />
                    </HtmlTooltip>
                  </Stack>

                  <Stack direction="row" gap="1rem" alignItems="baseline">
                    <TableSearch
                      setPageNumber={setNotificationGroupPageNumber}
                      setSavedListSettings={setNotificationGroupSavedListSettings}
                      searchRef={notificationGroupSearchRef}
                      savedListSettings={notificationGroupSavedListSettings}
                    />
                    <TableSelect
                      setPageNumber={setNotificationGroupPageNumber}
                      setSavedListSettings={setNotificationGroupSavedListSettings}
                      selectRef={notificationsGroupRoleRef}
                      savedListSettings={notificationGroupSavedListSettings}
                      id="notification-role"
                      label="Role"
                      options={Object.entries(vendorContactRoleHumanReadable).map(ent => ({
                        value: ent[0],
                        text: ent[1],
                      }))}
                      width={200}
                    />
                  </Stack>
                </div>
              }
              titleButtonText="Edit"
              titleButtonFunc={handleEditNotificationGroupClick}
              className={classes.cardStyles}
              classBody={classes.cardBody}
            >
              <Grid container>
                <Grid item xs={12}>
                  <Grid container justifyContent="space-between" alignItems="flex-end">
                    <Grid item md={12}>
                      {!!fields.notificationGroup.value && fields.notificationGroup.value ? (
                        <Grid container alignItems="center">
                          <Grid item md={12}>
                            <Table
                              color="primary"
                              data={filteredNotificationGroup}
                              getTrProps={(state, rowInfo, column, instance) => ({
                                style: { cursor: 'pointer' },
                                onClick: e => handleUserClick(rowInfo.original.dynamicsContactId),
                              })}
                              columns={[
                                {
                                  Header: 'Name',
                                  accessor: 'fullName',
                                  resizable: false,
                                },
                                {
                                  Header: 'Title',
                                  accessor: 'title',
                                },
                                {
                                  Header: 'Role',
                                  accessor: 'vendorContactRole',
                                  Cell: ({ value }) => (value ? vendorContactRoleHumanReadable[value] : null),
                                },
                                {
                                  Header: 'Email',
                                  accessor: 'email',
                                },
                                {
                                  Header: 'Phone',
                                  accessor: 'phoneNumber',
                                },
                                {
                                  Header: 'Notified On',
                                  accessor: 'applyingForReasons',
                                  Cell: ({ value }) =>
                                    value && value.length
                                      ? value.map(reason => reasonLabelByValue[reason] || reason).join(', ')
                                      : 'All applications',
                                },
                              ]}
                              onPageChange={changePage => {
                                setNotificationGroupPageNumber(changePage);
                              }}
                              onPageSizeChange={changePageSize => {
                                setNotificationGroupPageSizeValue(changePageSize);
                                setNotificationGroupPageNumber(0);
                              }}
                              page={notificationGroupPageNumber}
                              pageSize={notificationGroupPageSizeValue}
                              defaultPageSize={notificationGroupSavedListSettings.pageSize}
                              showPaginationTop={false}
                              showPaginationBottom={true}
                              customCellClasses={[classes.right]}
                              customClassesForCells={[5]}
                              className={classes.locationTable}
                            />
                          </Grid>
                          {editNotificationGroup && (
                            <Grid item xs={12}>
                              <br />
                              <Grid container justifyContent="space-between" alignItems="flex-end">
                                <Grid item md={12}>
                                  <p style={{ margin: '0 0 16px' }}>
                                    <b>Edit Staff For These Notifications</b>
                                  </p>
                                  <FormControl fullWidth>
                                    <Autocomplete
                                      multiple
                                      size="small"
                                      value={notificationGroup}
                                      options={fields.allReps.value
                                        .filter(rep => !notificationGroup.find(slr => slr.id === rep.id))
                                        .filter(user => user.status !== 'blocked')}
                                      onChange={(event, newValue) => {
                                        handleChangeNotificationReps(newValue);
                                      }}
                                      isOptionEqualToValue={(option, value) => {
                                        return option.id === value;
                                      }}
                                      getOptionLabel={option => {
                                        return option.label;
                                      }}
                                      renderInput={params => (
                                        <TextField {...params} variant="standard" label="Reps" placeholder="" />
                                      )}
                                    />
                                  </FormControl>
                                  {notificationGroup.length > 0 && (
                                    <Stack spacing={2} sx={{ mt: 3 }}>
                                      <Typography variant="subtitle2">Notified On (Reason for Applying)</Typography>
                                      {notificationGroup.map(member => (
                                        <Grid container spacing={1} alignItems="center" key={member.id}>
                                          <Grid item xs={12} sm={4}>
                                            <Typography variant="body2">
                                              {member.label || member.fullName || member.email}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={12} sm={8}>
                                            <Autocomplete
                                              multiple
                                              size="small"
                                              options={applyingForOptions}
                                              value={applyingForOptions.filter(option =>
                                                (member.applyingForReasons || []).includes(option.value)
                                              )}
                                              getOptionLabel={option => option.label}
                                              isOptionEqualToValue={(option, value) => option.value === value.value}
                                              onChange={(event, newValue) =>
                                                handleChangeMemberReasons(
                                                  member.id,
                                                  newValue.map(option => option.value)
                                                )
                                              }
                                              renderInput={params => (
                                                <TextField
                                                  {...params}
                                                  variant="standard"
                                                  placeholder={
                                                    (member.applyingForReasons || []).length
                                                      ? undefined
                                                      : 'All applications'
                                                  }
                                                />
                                              )}
                                            />
                                          </Grid>
                                        </Grid>
                                      ))}
                                    </Stack>
                                  )}
                                  <br />
                                  <br />
                                  <Grid item md={12} className={classes.companyProfileContact}>
                                    <Grid
                                      container
                                      style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', gap: 8 }}
                                    >
                                      <Button
                                        onClick={handleCancelEditNotificationClick}
                                        disabled={savingNotificationGroup}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        color="primary"
                                        variant="contained"
                                        onClick={handleSaveEditNotificationClick}
                                        disabled={savingNotificationGroup}
                                      >
                                        {savingNotificationGroup ? (
                                          <>
                                            <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
                                            Saving
                                          </>
                                        ) : (
                                          'Save'
                                        )}
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                      ) : (
                        'No users are currently configured to be notified.  Please EDIT to add desired recipients.'
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Tile>
          </Grid>

          <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleClose}>
            <Alert onClose={handleClose} severity="success" variant="filled">
              Successfully updated the Location!
            </Alert>
          </Snackbar>
          <Snackbar open={errorOpen} autoHideDuration={6000} onClose={handleClose}>
            <Alert onClose={handleClose} severity="error" variant="filled">
              {errorMessage}
            </Alert>
          </Snackbar>

          <Modal open={openEditLocationModal} onClose={handleCloseEditLocation}>
            <CreateLocationModal
              locationDetails={locationDetails}
              locationsList={locationsList}
              account={account}
              closeCreateLocationModal={handleCloseEditLocation}
            />
          </Modal>
        </>
      ) : (
        <span>
          <CircularProgress size={15} /> Loading Location Details
        </span>
      )}
    </PageContainer>
  );
}

const mapDispatchToProps = {
  setRefetch,
};

export default connect(null, mapDispatchToProps)(LocationDetails);
